import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Armchair, Sparkles, Lock, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import LegalGate, { hasAcceptedLegal } from "@/components/LegalGate";
import { useT } from "@/lib/i18n";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Algo falló. Inténtalo de nuevo.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

function AdminLoginDialog({ onClose }) {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(true);
  const [busy, setBusy] = useState(false);

  // Pre-fill email from localStorage if saved previously
  React.useEffect(() => {
    try {
      const saved = localStorage.getItem("canales_admin_email");
      if (saved) setEmail(saved);
    } catch {}
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error("Completa correo y contraseña");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login", { email, password, remember });
      setUser(data.user);
      // Save email if user wants to be remembered (not the password — browser handles that)
      try {
        if (remember) localStorage.setItem("canales_admin_email", email);
        else localStorage.removeItem("canales_admin_email");
      } catch {}
      toast.success(`Bienvenido, ${data.user.name}`);
      if (data.user.must_change_password) {
        navigate("/change-password", { replace: true });
      } else {
        navigate(data.user.role === "admin" ? "/admin" : "/dashboard", { replace: true });
      }
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail) || "No se pudo iniciar sesión";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#1A1A1A]/40 backdrop-blur-sm p-4" data-testid="admin-login-dialog">
      <div className="bg-white border border-[#E5E7EB] rounded-md w-full max-w-sm p-6 relative shadow-xl">
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-[#737373] hover:text-[#1A1A1A]"
          data-testid="admin-login-close"
          type="button"
        >
          <X className="h-4 w-4" />
        </button>
        <div className="flex items-center gap-2 mb-1">
          <Lock className="h-4 w-4 text-[#1B2E7A]" strokeWidth={1.5} />
          <p className="text-xs uppercase tracking-[0.2em] text-[#6B7280]">Acceso administrador</p>
        </div>
        <h2 className="font-heading text-2xl text-[#1A1A1A] mb-2">Inicia sesión</h2>
        <p className="text-sm text-[#374151] mb-5">
          Ingresa con tu cuenta institucional para gestionar la plataforma.
        </p>
        <form
          onSubmit={submit}
          method="post"
          action="/api/auth/login"
          className="space-y-3"
          autoComplete="on"
        >
          <Input
            id="admin-email"
            name="email"
            type="email"
            placeholder="correo@dominio.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            data-testid="admin-email-input"
            autoComplete="username"
            required
          />
          <Input
            id="admin-password"
            name="password"
            type="password"
            placeholder="Contraseña"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            data-testid="admin-password-input"
            autoComplete="current-password"
            required
          />
          <label className="flex items-center gap-2 text-sm text-[#374151] cursor-pointer select-none pt-1">
            <input
              type="checkbox"
              checked={remember}
              onChange={(e) => setRemember(e.target.checked)}
              className="accent-[#1B2E7A] h-4 w-4"
              data-testid="remember-checkbox"
            />
            Recordarme en este dispositivo (90 días)
          </label>
          <Button
            type="submit"
            disabled={busy}
            className="w-full bg-[#1B2E7A] hover:bg-[#13205A] text-white"
            data-testid="admin-submit-button"
          >
            {busy ? "Validando..." : "Entrar como admin"}
          </Button>
          <div className="flex items-center justify-between pt-1">
            <p className="text-xs text-[#6B7280]">
              Tu navegador te ofrecerá guardar la contraseña al entrar.
            </p>
            <button
              type="button"
              onClick={() => navigate("/forgot-password")}
              className="text-xs text-[#1B2E7A] hover:text-[#13205A] hover:underline whitespace-nowrap ml-2"
              data-testid="forgot-password-link"
            >
              ¿Olvidaste tu contraseña?
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function Login() {
  const t = useT();
  const [showAdmin, setShowAdmin] = useState(false);
  const [legalAccepted, setLegalAccepted] = useState(() => hasAcceptedLegal());

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen w-full flex" data-testid="login-page">
      {/* Left side - hero image */}
      <div
        className="hidden md:flex md:w-3/5 relative bg-cover bg-center"
        style={{
          backgroundImage:
            "url('https://images.unsplash.com/photo-1760611656148-063d3b9a8dbc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1Mjh8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBmdXJuaXR1cmUlMjBzdG9yZXxlbnwwfHx8fDE3NzcyMzIzNzd8MA&ixlib=rb-4.1.0&q=85')",
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-[#1A1A1A]/80 via-[#1A1A1A]/40 to-transparent" />
        <div className="relative z-10 flex flex-col justify-between p-12 text-white w-full">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-md bg-white overflow-hidden p-0.5 shadow-md">
              <img src="/icon-192.png" alt="Canales" className="h-full w-full object-contain" />
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-white/70">Mueblería</p>
              <p className="font-heading text-xl">Canales</p>
            </div>
          </div>
          <div className="max-w-lg">
            <p className="text-xs uppercase tracking-[0.2em] text-white/70 mb-4">
              {t("login.eyebrow_left")}
            </p>
            <h1 className="font-heading text-4xl sm:text-5xl font-medium tracking-tight mb-4">
              {t("login.hero_title")}
            </h1>
            <p className="text-white/80 text-base leading-relaxed">
              {t("login.hero_subtitle")}
            </p>
          </div>
          <p className="text-xs text-white/60">© {new Date().getFullYear()} Mueblería Canales — {t("login.footer_left")}</p>
        </div>
      </div>

      {/* Right side - login */}
      <div className="w-full md:w-2/5 flex items-center justify-center p-8 bg-[#FFFFFF]">
        <div className="w-full max-w-sm fade-up">
          <div className="md:hidden flex items-center gap-3 mb-12">
            <div className="h-12 w-12 rounded-md bg-white border border-[#E5E7EB] overflow-hidden p-0.5">
              <img src="/icon-192.png" alt="Canales" className="h-full w-full object-contain" />
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-[#6B7280]">Mueblería</p>
              <p className="font-heading text-xl text-[#1A1A1A]">Canales</p>
            </div>
          </div>
          <p className="text-xs uppercase tracking-[0.2em] text-[#6B7280] mb-3">{t("login.team_access")}</p>
          <h2 className="font-heading text-3xl sm:text-4xl font-medium tracking-tight text-[#1A1A1A] mb-3">
            {t("login.welcome_back")}
          </h2>
          <p className="text-[#374151] mb-10 leading-relaxed">
            {t("login.welcome_subtitle")}
          </p>

          <Button
            onClick={handleGoogleLogin}
            data-testid="google-login-button"
            className="w-full h-12 bg-[#1B2E7A] hover:bg-[#13205A] text-white font-medium rounded-md transition-all duration-200"
          >
            <svg className="h-5 w-5 mr-3" viewBox="0 0 24 24">
              <path fill="#fff" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#fff" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" opacity="0.9"/>
              <path fill="#fff" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" opacity="0.7"/>
              <path fill="#fff" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z" opacity="0.85"/>
            </svg>
            {t("login.google_btn")}
          </Button>

          <div className="relative my-5">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t border-[#E5E7EB]" />
            </div>
            <div className="relative flex justify-center">
              <span className="bg-[#FFFFFF] px-3 text-[10px] uppercase tracking-[0.2em] text-[#6B7280]">{t("login.or")}</span>
            </div>
          </div>

          <Button
            onClick={() => setShowAdmin(true)}
            data-testid="open-admin-login"
            variant="outline"
            className="w-full h-12 border-[#1B2E7A] text-[#1B2E7A] hover:bg-[#F3F4F6] font-medium rounded-md transition-all duration-200 bg-transparent"
          >
            <Lock className="h-4 w-4 mr-2" />
            {t("login.admin_btn")}
          </Button>

          <div className="mt-6 p-4 rounded-md bg-[#F3F4F6] border border-[#E5E7EB]">
            <div className="flex gap-3">
              <Sparkles className="h-4 w-4 text-[#6B7280] mt-0.5 shrink-0" strokeWidth={1.5} />
              <p className="text-sm text-[#374151] leading-relaxed">
                {t("login.help_text")}
              </p>
            </div>
          </div>

          <p className="text-xs text-[#6B7280] mt-10 leading-relaxed">
            {t("login.legal_prefix")}{" "}
            <a href="/privacy" className="text-[#1B2E7A] underline hover:no-underline" data-testid="login-privacy-link">
              {t("login.privacy_link")}
            </a>
            {" "}{t("login.legal_and")}{" "}
            <a href="/terms" className="text-[#1B2E7A] underline hover:no-underline" data-testid="login-terms-link">
              {t("login.terms_link")}
            </a>
            {" "}{t("login.legal_suffix")}
          </p>
        </div>
      </div>

      {showAdmin && <AdminLoginDialog onClose={() => setShowAdmin(false)} />}
      {!legalAccepted && (
        <LegalGate
          onAccept={({ version, locale }) => {
            setLegalAccepted(true);
            // Best-effort audit log — ignore failures so user isn't blocked
            try {
              api.post("/legal/accept", { version, locale }).catch(() => {});
            } catch { /* noop */ }
          }}
        />
      )}
    </div>
  );
}
